import { ListedInstrument, OrderType, useDictionary } from '@eih-netbroker/common';
import { ButtonBase } from '@mui/material';
import { memo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { useOptions } from '../../../../../shared/services/useOptions';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import classes from './OrderFormTypeSelector.module.scss';

const defaultOptions: Array<OrderType> = [
  OrderType.Market,
  OrderType.Limit,
  OrderType.StopMarket,
  OrderType.StopLimit,
  OrderType.Oco,
] as const;

export type OrderFormTypeSelectorProps = {
  instrument?: ListedInstrument | null;
  selectedValue?: OrderType;
  onChange: (value: OrderType) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
};

export const OrderFormTypeSelector = memo(
  ({ instrument, selectedValue, onChange, disabled, readOnly, className }: OrderFormTypeSelectorProps) => {
    const { translation } = useLocale();
    const { getOrderTypeOptions } = useOptions();
    const { getDictionary } = useDictionary();
    const orderTypeOptions = getDictionary('orderTypes');

    const loadedOptions = getOrderTypeOptions(instrument);
    const options =
      loadedOptions.length > 0
        ? loadedOptions
        : orderTypeOptions.filter(option => defaultOptions.includes(option.id as OrderType));

    const handleClick = (value: OrderType) => {
      if (readOnly || disabled || value === selectedValue) {
        return;
      }
      onChange(value);
    };

    return (
      <div className={cssWhen(classes.container, className)}>
        <span
          className={classes.label}
          data-testid="order-form-order-type-label"
        >
          {translation.orderModal.orderType}
        </span>
        <div
          className={cssWhen(classes.buttonRow, [disabled, classes.buttonRowIsDisabled])}
          data-testid="order-form-order-type-list"
        >
          {options.map(option => (
            <ButtonBase
              key={String(option.id)}
              className={cssWhen(classes.button, [selectedValue === option.id, classes.buttonIsActive])}
              onClick={() => handleClick(option.id as OrderType)}
              disabled={disabled}
              disableRipple
              data-testid={`order-form-order-type-option-${String(option.id).toLowerCase()}`}
            >
              {option.name}
            </ButtonBase>
          ))}
        </div>
      </div>
    );
  },
);
